import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import api from '../api/axiosConfig';
import { AdminAccountDto, AdminAccountsDto, VersionStatus } from '../types';

const roleLabels: Record<string, string> = {
    ROLE_ADMIN: 'Администратор', ADMIN: 'Администратор',
    ROLE_CLINIC_HEAD: 'Главный врач', CLINIC_HEAD: 'Главный врач',
    ROLE_DOCTOR: 'Врач', DOCTOR: 'Врач',
};

const statusLabels: Record<VersionStatus, string> = {
    CURRENT: 'Актуальная',
    OUTDATED: 'Устарела',
    UNKNOWN: 'Не сообщал',
};

const statusVariants: Record<VersionStatus, string> = {
    CURRENT: 'success',
    OUTDATED: 'danger',
    UNKNOWN: 'secondary',
};

const platformLabels: Record<string, string> = {
    android: 'Android', iOS: 'iOS', macOS: 'macOS', windows: 'Windows', linux: 'Linux', fuchsia: 'Fuchsia',
};

const formatDateTime = (value?: string): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const formatDate = (value?: string): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('ru-RU');
};

/** "Сегодня" / "3 дн. назад" — the shape an admin actually scans for. */
const formatStaleness = (days?: number): string => {
    if (days === undefined || days === null) return 'ни разу';
    if (days <= 0) return 'сегодня';
    if (days === 1) return 'вчера';
    return `${days} дн. назад`;
};

const stalenessVariant = (days?: number): string => {
    if (days === undefined || days === null) return 'text-danger';
    if (days <= 7) return 'text-success';
    if (days <= 30) return 'text-warning';
    return 'text-danger';
};

const Accounts: React.FC = () => {
    const [data, setData] = useState<AdminAccountsDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [onlyOutdated, setOnlyOutdated] = useState(false);
    const [hideTest, setHideTest] = useState(true);
    const [hideAdmins, setHideAdmins] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<AdminAccountsDto>('/admin/accounts');
            setData(response.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить аналитику по аккаунтам');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const accounts: AdminAccountDto[] = useMemo(() => data?.accounts ?? [], [data]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return accounts
            .filter((a) => !hideTest || !a.testAccount)
            .filter((a) => !hideAdmins || !a.role?.includes('ADMIN'))
            .filter((a) => !onlyOutdated || a.versionStatus !== 'CURRENT')
            .filter((a) => !needle || [a.username, a.fullName, a.email, a.phone, a.appVersionLabel]
                .some((value) => value?.toLowerCase().includes(needle)));
    }, [accounts, hideAdmins, hideTest, onlyOutdated, query]);

    // Cards describe the filtered set, so toggling "скрыть тестовые" doesn't leave the
    // headline numbers disagreeing with the table underneath them.
    const summary = useMemo(() => ({
        total: filtered.length,
        current: filtered.filter((a) => a.versionStatus === 'CURRENT').length,
        outdated: filtered.filter((a) => a.versionStatus === 'OUTDATED').length,
        unknown: filtered.filter((a) => a.versionStatus === 'UNKNOWN').length,
        active7: filtered.filter((a) => a.daysSinceLastSeen !== undefined && a.daysSinceLastSeen !== null && a.daysSinceLastSeen < 7).length,
        disabled: filtered.filter((a) => !a.enabled).length,
    }), [filtered]);

    const versionChart = useMemo(() => {
        const counts = new Map<string, number>();
        filtered.forEach((a) => {
            const key = a.appVersionLabel || 'Не сообщал';
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        return Array.from(counts.entries()).map(([version, count]) => ({
            version,
            count,
            latest: !!data?.latestVersion && version === data.latestVersion,
        }));
    }, [data, filtered]);

    if (loading) {
        return <Container className="d-flex justify-content-center mt-5"><Spinner animation="border" /></Container>;
    }

    return (
        <Container className="mt-4">
            <div className="page-heading">
                <div>
                    <h2><i className="bi bi-graph-up-arrow text-primary me-2" />Аккаунты</h2>
                    <div className="muted-small">Версия приложения и активность по каждой учётной записи</div>
                </div>
                <Button variant="outline-secondary" onClick={load}><i className="bi bi-arrow-clockwise me-2" />Обновить</Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Alert variant={data?.latestVersionSource === 'CONFIGURED' ? 'primary' : 'secondary'} className="d-flex align-items-center gap-2">
                <i className="bi bi-box-seam" />
                <div>
                    Эталонная версия: <strong>{data?.latestVersion || 'нет данных'}</strong>{' '}
                    <span className="text-muted small">
                        {data?.latestVersionSource === 'CONFIGURED'
                            ? '— задана в настройках сервера (app.mobile.latest-version)'
                            : '— самая новая из тех, что сообщили устройства. Пока никто не обновился, все выглядят актуальными: задайте app.mobile.latest-version при выпуске релиза.'}
                    </span>
                </div>
            </Alert>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <Card className="text-center shadow-sm h-100"><Card.Body>
                        <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Аккаунтов</Card.Title>
                        <h3 className="text-primary mb-0">{summary.total}</h3>
                        <div className="muted-small">{summary.disabled > 0 ? `${summary.disabled} отключено` : 'все активны'}</div>
                    </Card.Body></Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm h-100"><Card.Body>
                        <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>На актуальной версии</Card.Title>
                        <h3 className="text-success mb-0">{summary.current}</h3>
                        <div className="muted-small">из {summary.total}</div>
                    </Card.Body></Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm h-100"><Card.Body>
                        <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Нужно обновить</Card.Title>
                        <h3 className="text-danger mb-0">{summary.outdated}</h3>
                        <div className="muted-small">{summary.unknown} без данных о версии</div>
                    </Card.Body></Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm h-100"><Card.Body>
                        <Card.Title className="text-muted" style={{ fontSize: '1rem' }}>Заходили за 7 дней</Card.Title>
                        <h3 className="text-info mb-0">{summary.active7}</h3>
                        <div className="muted-small">по всем: {data?.activeLast30Days ?? 0} за 30 дней</div>
                    </Card.Body></Card>
                </Col>
            </Row>

            <Card className="shadow-sm mb-4"><Card.Body>
                <Card.Title>Распределение по версиям</Card.Title>
                <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={versionChart}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="version" />
                            <YAxis allowDecimals={false} />
                            <ChartTooltip formatter={(value: any) => [value, 'Аккаунтов']} />
                            <Bar dataKey="count" name="Аккаунтов">
                                {versionChart.map((entry) => (
                                    <Cell key={entry.version} fill={entry.latest ? '#198754' : '#adb5bd'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card.Body></Card>

            <Card className="shadow-sm mb-3"><Card.Body>
                <Row className="g-2 align-items-center">
                    <Col md={6}>
                        <InputGroup>
                            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                            <Form.Control placeholder="Поиск по имени, логину, email или версии" value={query} onChange={(e) => setQuery(e.target.value)} />
                        </InputGroup>
                    </Col>
                    <Col md={6} className="d-flex flex-wrap gap-3 justify-content-md-end">
                        <Form.Check type="switch" id="only-outdated" label="Только требующие обновления" checked={onlyOutdated} onChange={(e) => setOnlyOutdated(e.target.checked)} />
                        <Form.Check type="switch" id="hide-test" label="Скрыть тестовые" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} />
                        <Form.Check type="switch" id="hide-admins" label="Скрыть админов" checked={hideAdmins} onChange={(e) => setHideAdmins(e.target.checked)} />
                    </Col>
                </Row>
            </Card.Body></Card>

            <Card className="shadow-sm"><Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Пользователь</th>
                            <th>Роль</th>
                            <th>Версия приложения</th>
                            <th>Обновился</th>
                            <th>Последняя активность</th>
                            <th className="text-end">Пациенты</th>
                            <th className="text-end">Сессии</th>
                            <th className="text-end">Снимки</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((a) => (
                            <tr key={a.id}>
                                <td>
                                    <div className="fw-semibold">{a.fullName || a.username}</div>
                                    <code>{a.username}</code>
                                    {!a.enabled && <Badge bg="dark" className="ms-2">отключён</Badge>}
                                    {a.testAccount && <Badge bg="light" text="dark" className="ms-2">тестовый</Badge>}
                                    <div className="muted-small">{a.organizations?.join(', ') || '—'}</div>
                                </td>
                                <td><Badge bg={a.role?.includes('ADMIN') ? 'primary' : a.role?.includes('HEAD') ? 'info' : 'secondary'}>{roleLabels[a.role] || a.role}</Badge></td>
                                <td>
                                    <Badge bg={statusVariants[a.versionStatus]}>
                                        {a.appVersionLabel || statusLabels[a.versionStatus]}
                                    </Badge>
                                    {a.appVersionLabel && a.versionStatus !== 'CURRENT' && (
                                        <span className="muted-small ms-2">{statusLabels[a.versionStatus]}</span>
                                    )}
                                    <div className="muted-small">
                                        {a.appPlatform ? (platformLabels[a.appPlatform] || a.appPlatform) : '—'}
                                        {a.osVersion && (
                                            <OverlayTrigger overlay={<Tooltip>{a.osVersion}</Tooltip>}>
                                                <span className="ms-1 text-truncate d-inline-block align-bottom" style={{ maxWidth: 160 }}>· {a.osVersion}</span>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                </td>
                                <td>{formatDate(a.appVersionUpdatedAt)}</td>
                                <td>
                                    <div className={stalenessVariant(a.daysSinceLastSeen)}>{formatStaleness(a.daysSinceLastSeen)}</div>
                                    <div className="muted-small">{formatDateTime(a.lastSeenAt)}</div>
                                </td>
                                <td className="text-end">{a.patientsCount}</td>
                                <td className="text-end">{a.sessionsCount}</td>
                                <td className="text-end">
                                    {a.detailedImagesCount}
                                    <div className="muted-small">{a.lastImageAt ? formatDate(a.lastImageAt) : '—'}</div>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-muted py-4">Аккаунты не найдены</td></tr>
                        )}
                    </tbody>
                </Table>
            </Card.Body></Card>
        </Container>
    );
};

export default Accounts;
